import { formatRequestReference, getExchangeRateLabel, getStatusHeroMeta } from './transactionDetailHelpers';
import { formatPaymentPurpose, formatTransferDate, formatAccountDisplay, getBankLabel, normalizeCrossBorderTransaction } from './recipientHelpers';
import { formatNumberWithDecimalLayout, getDecimalLayoutLocale } from './userPreferencesHelpers';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatReceiptAmount(amount, currencyCode, preferences) {
  if (amount == null || amount === '' || !currencyCode) return '—';
  const num = typeof amount === 'number' ? amount : Number.parseFloat(String(amount).replace(/,/g, ''));
  if (!Number.isFinite(num)) return '—';

  if (currencyCode === 'EUR') {
    return `${formatNumberWithDecimalLayout(num, preferences)} €`;
  }

  const locale = getDecimalLayoutLocale(preferences);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${formatNumberWithDecimalLayout(num, preferences)} ${currencyCode}`;
  }
}

function formatReceiptDateTime(dateInput) {
  if (!dateInput) return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date());

  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function buildDetailRow(label, value) {
  if (value == null || value === '' || value === '—') return '';
  return `
    <tr>
      <th scope="row">${escapeHtml(label)}</th>
      <td>${escapeHtml(value)}</td>
    </tr>`;
}

export function buildTransferReceiptHtml(receipt, preferences) {
  const reference = receipt.reference || formatRequestReference(receipt.requestId);
  const statusLabel = receipt.statusLabel || 'Transfer';
  const generatedAt = formatReceiptDateTime(receipt.generatedAt);
  const sendAmount = formatReceiptAmount(receipt.sendAmount, receipt.sendCurrency, preferences);
  const receiveAmount = formatReceiptAmount(receipt.receiveAmount, receipt.receiveCurrency, preferences);
  const fee =
    receipt.fee != null && receipt.fee !== ''
      ? formatReceiptAmount(receipt.fee, receipt.feeCurrency || receipt.sendCurrency || 'EUR', preferences)
      : null;
  const debitedCurrency = receipt.sendCurrency || 'EUR';
  let totalDebited = null;
  if (receipt.totalDebited != null) {
    totalDebited = formatReceiptAmount(receipt.totalDebited, debitedCurrency, preferences);
  } else if (fee && receipt.sendAmount != null) {
    totalDebited = formatReceiptAmount(Number(receipt.sendAmount) + Number(receipt.fee), debitedCurrency, preferences);
  }

  const detailRows = [
    buildDetailRow('Date', receipt.dateSent ? formatTransferDate(receipt.dateSent) : generatedAt.split(',')[0]),
    buildDetailRow('Time', receipt.dateSent ? formatReceiptDateTime(receipt.dateSent).split(', ').slice(1).join(', ') : generatedAt.split(', ').slice(1).join(', ')),
    buildDetailRow('Date settled', receipt.dateSettled ? formatTransferDate(receipt.dateSettled) : null),
    buildDetailRow('Recipient', receipt.recipientName),
    buildDetailRow('Destination', receipt.destinationCountry),
    buildDetailRow('Account', receipt.accountDisplay),
    buildDetailRow('Bank', receipt.bankLabel),
    buildDetailRow('Transfer method', receipt.transferMethod),
    buildDetailRow('Payment reference', receipt.paymentReference),
    buildDetailRow('Payment purpose', receipt.paymentPurpose),
    buildDetailRow('Exchange rate', receipt.exchangeRate),
    buildDetailRow('Fee', fee),
    buildDetailRow('Total debited', totalDebited),
  ].join('');

  const title = receipt.title || 'Transfer receipt';

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)} · ${escapeHtml(reference)}</title>
    <style>
      @page { margin: 18mm 16mm; size: A4; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        padding: 0;
        color: #0b1929;
        font-family: "Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
        font-size: 13px;
        line-height: 1.45;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .sheet {
        max-width: 720px;
        margin: 0 auto;
      }
      .header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 24px;
        padding-bottom: 20px;
        border-bottom: 2px solid #0b1929;
        margin-bottom: 24px;
      }
      .brand {
        font-family: Georgia, "Times New Roman", serif;
        font-size: 28px;
        font-weight: 400;
        letter-spacing: -0.02em;
        margin: 0;
      }
      .brand-sub {
        margin: 4px 0 0;
        font-size: 11px;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: #64748b;
      }
      .meta {
        text-align: right;
        font-size: 12px;
        color: #475569;
      }
      .meta strong {
        display: block;
        color: #0b1929;
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 4px;
      }
      .status {
        display: inline-block;
        margin-top: 8px;
        padding: 4px 10px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        background: #f1f5f9;
        color: #0b1929;
      }
      .amounts {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
        margin-bottom: 28px;
      }
      .amount-card {
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        padding: 18px 20px;
        background: #f8fafc;
      }
      .amount-card.primary {
        background: #0b1929;
        border-color: #0b1929;
        color: #fff;
      }
      .amount-label {
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: #64748b;
        margin-bottom: 8px;
      }
      .amount-card.primary .amount-label {
        color: rgba(255, 255, 255, 0.62);
      }
      .amount-value {
        font-family: Georgia, "Times New Roman", serif;
        font-size: 30px;
        font-weight: 400;
        letter-spacing: -0.02em;
        line-height: 1.1;
      }
      .amount-currency {
        font-size: 14px;
        opacity: 0.72;
        margin-left: 6px;
      }
      .section-title {
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: #94a3b8;
        margin: 0 0 10px;
      }
      table.details {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 28px;
      }
      table.details th,
      table.details td {
        padding: 10px 0;
        border-bottom: 1px solid #e8edf2;
        text-align: left;
        vertical-align: top;
      }
      table.details th {
        width: 38%;
        font-size: 12px;
        font-weight: 500;
        color: #64748b;
      }
      table.details td {
        font-size: 13px;
        font-weight: 500;
        color: #0b1929;
        word-break: break-word;
      }
      .footer {
        margin-top: 32px;
        padding-top: 16px;
        border-top: 1px solid #e2e8f0;
        font-size: 11px;
        color: #94a3b8;
        line-height: 1.55;
      }
      .footer strong { color: #64748b; }
    </style>
  </head>
  <body>
    <div class="sheet">
      <header class="header">
        <div>
          <h1 class="brand">HansePay</h1>
          <p class="brand-sub">Transfer receipt</p>
        </div>
        <div class="meta">
          <strong>${escapeHtml(reference)}</strong>
          <div>Generated ${escapeHtml(generatedAt)}</div>
          <span class="status">${escapeHtml(statusLabel)}</span>
        </div>
      </header>

      <div class="amounts">
        <div class="amount-card">
          <div class="amount-label">You sent</div>
          <div class="amount-value">
            ${escapeHtml(sendAmount)}
            ${receipt.sendCurrency ? `<span class="amount-currency">${escapeHtml(receipt.sendCurrency)}</span>` : ''}
          </div>
        </div>
        <div class="amount-card primary">
          <div class="amount-label">Recipient gets</div>
          <div class="amount-value">
            ${escapeHtml(receiveAmount)}
            ${receipt.receiveCurrency ? `<span class="amount-currency">${escapeHtml(receipt.receiveCurrency)}</span>` : ''}
          </div>
        </div>
      </div>

      <h2 class="section-title">Transfer details</h2>
      <table class="details">
        <tbody>
          ${detailRows}
        </tbody>
      </table>

      <footer class="footer">
        <strong>Important</strong><br />
        This receipt confirms your transfer instruction with HansePay. It is not a bank guarantee of settlement timing.
        Keep this document for your records. For support, quote reference <strong>${escapeHtml(reference)}</strong>.
      </footer>
    </div>
  </body>
</html>`;
}

export function printTransferReceipt(receiptData, preferences) {
  const html = buildTransferReceiptHtml(receiptData, preferences);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const blobUrl = URL.createObjectURL(blob);

  const printWindow = window.open(blobUrl, '_blank');
  if (!printWindow) {
    URL.revokeObjectURL(blobUrl);
    throw new Error('Could not open print window. Please allow popups to download your receipt.');
  }

  const cleanup = () => URL.revokeObjectURL(blobUrl);

  const triggerPrint = () => {
    printWindow.focus();
    printWindow.print();
    printWindow.onafterprint = () => {
      printWindow.close();
      cleanup();
    };
  };

  const schedulePrint = () => setTimeout(triggerPrint, 300);

  try {
    if (printWindow.document?.readyState === 'complete') {
      schedulePrint();
    } else {
      printWindow.addEventListener('load', schedulePrint, { once: true });
    }
  } catch {
    // Cross-origin guard — blob should still render; try print after a short delay.
    schedulePrint();
  }
}

export function receiptFromTransaction(transaction, extras = {}) {
  const tx = normalizeCrossBorderTransaction(transaction);
  const statusMeta = getStatusHeroMeta(tx.status);
  const sendCurrency = tx.baseCurrency || 'EUR';
  const receiveCurrency = tx.quoteCurrency || extras.recipient?.currency || '';
  const fee = Number(tx.totalFee) || 0;
  const sendAmount = Math.abs(Number(tx.amountIn)) || 0;

  return {
    requestId: tx.requestId,
    reference: tx.paymentReference?.trim() || formatRequestReference(tx.requestId),
    statusLabel: statusMeta.label,
    title: 'Transfer receipt',
    dateSent: tx.started || tx.dateAdded,
    dateSettled: tx.finished || null,
    sendAmount,
    sendCurrency,
    receiveAmount: tx.amountOut,
    receiveCurrency,
    fee,
    feeCurrency: sendCurrency,
    totalDebited: sendAmount + fee,
    recipientName: extras.recipient?.name || tx.recipient?.name,
    destinationCountry: extras.countryName,
    accountDisplay: extras.accountDisplay || (extras.recipient ? formatAccountDisplay(extras.recipient) : null),
    bankLabel: extras.bankLabel || (extras.recipient ? getBankLabel(extras.recipient) : null),
    transferMethod: extras.transferMethod,
    paymentReference: tx.paymentReference?.trim() || formatRequestReference(tx.requestId),
    paymentPurpose: extras.paymentPurposeLabel || formatPaymentPurpose(tx.paymentPurpose),
    exchangeRate: extras.exchangeRate || getExchangeRateLabel(tx),
    generatedAt: new Date(),
  };
}

export function receiptFromSendProgress({
  requestId,
  recipientName,
  destinationCountry,
  sendAmount,
  sendCurrency,
  receiveAmount,
  receiveCurrency,
  paymentReference,
  paymentPurpose,
  transferFee,
  exchangeRate,
  statusLabel = 'In transit',
}) {
  const fee = transferFee != null ? Number(transferFee) : null;
  const sendNum = sendAmount != null ? Number(sendAmount) : null;

  return {
    requestId,
    reference: paymentReference?.trim() || formatRequestReference(requestId),
    statusLabel,
    title: 'Payment confirmation',
    dateSent: new Date(),
    sendAmount: sendNum,
    sendCurrency: sendCurrency || 'EUR',
    receiveAmount,
    receiveCurrency,
    fee,
    feeCurrency: sendCurrency || 'EUR',
    totalDebited: sendNum != null && fee != null ? sendNum + fee : sendNum,
    recipientName,
    destinationCountry: destinationCountry?.name || destinationCountry || null,
    paymentReference: paymentReference?.trim() || formatRequestReference(requestId),
    paymentPurpose,
    exchangeRate,
    generatedAt: new Date(),
  };
}
